<template>
  <div class="p-4 sm:p-6">
    <!-- Header -->
    <div class="mb-6 sm:mb-8">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Claims Management</h1>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
      <!-- Total Claims -->
      <div class="bg-white dark:bg-black rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Total Claims</p>
            <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">
              {{ pagination.total || 0 }}
            </p>
          </div>
          <div class="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path fill-rule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Under Review -->
      <div class="bg-white dark:bg-black rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Under Review</p>
            <p class="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mt-2">
              {{ underReviewClaims.length }}
            </p>
          </div>
          <div class="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Approved -->
      <div class="bg-white dark:bg-black rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Approved</p>
            <p class="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
              {{ approvedClaims.length }}
            </p>
          </div>
          <div class="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Rejected -->
      <div class="bg-white dark:bg-black rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Rejected</p>
            <p class="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">
              {{ rejectedClaims.length }}
            </p>
          </div>
          <div class="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <ClaimFilters
      v-model:search="searchQuery"
      v-model:status="statusFilter"
      v-model:brand="brandFilter"
      v-model:dateFrom="dateFromFilter"
      v-model:dateTo="dateToFilter"
      @apply="applyFilters"
      @reset="resetFilters"
    />

    <!-- Data Display: Desktop Table / Mobile Cards -->
    <div class="bg-white dark:bg-black rounded-lg shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">

      <!-- Loading State -->
      <div v-if="loading">
        <!-- Desktop Table Skeleton -->
        <SkeletonLoader type="table" :rows="5" :columns="9" />

        <!-- Mobile Card Skeleton -->
        <SkeletonLoader type="card" :rows="5" />
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-16 px-6">
        <svg xmlns="http://www.w3.org/2000/svg" class="mx-auto h-16 w-16 text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">Error loading claims</h3>
        <p class="text-red-500 dark:text-red-400 mb-6 max-w-sm mx-auto">{{ error }}</p>
        <button
          class="bg-primary hover:bg-primary-hover text-white px-6 py-3 rounded-lg transition-colors duration-200 shadow-sm"
          @click="claimStore.fetchClaims()"
        >
          Try Again
        </button>
      </div>

      <!-- Claim Table -->
      <div v-else-if="claims.length > 0">
        <ClaimTable
          :claims="claims"
          :loading="loading"
          @view="viewClaim"
          @edit="openEditModal"
          @approve="openApproveModal"
          @reject="openRejectModal"
          @delete="openDeleteModal"
        />
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="text-center py-16 px-6"
      >
        <div
          class="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
          :style="{ backgroundColor: brandColors.primary + '20' }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-10 w-10 sm:h-12 sm:w-12"
            :style="{ color: brandColors.primary }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
          {{ searchQuery || statusFilter || brandFilter || dateFromFilter || dateToFilter ? 'No Claims Match Your Search' : 'No Claims Found' }}
        </h3>
        <p class="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6 max-w-sm mx-auto">
          {{ searchQuery || statusFilter || brandFilter || dateFromFilter || dateToFilter ? 'Try adjusting your filters to find what you\'re looking for.' : 'No warranty claims have been submitted yet.' }}
        </p>
      </div>

      <!-- Pagination -->
      <div v-if="claims.length > 0">
        <CustomerPagination
          :current-page="pagination.current_page"
          :last-page="pagination.last_page"
          :per-page="pagination.per_page"
          :total="pagination.total"
          @prev="goToPreviousPage"
          @next="goToNextPage"
          @goto="goToPage"
        />
      </div>

    </div>
  </div>

  <!-- Claim Details Modal -->
  <ClaimDetailsModal
    :is-open="showDetailsModal"
    :claim="selectedClaim"
    @close="closeDetailsModal"
    @edit="openEditModalFromDetails"
  />

  <!-- Edit Claim Modal -->
  <ClaimEditModal
    :is-open="showEditModal"
    :claim="claimToEdit"
    :loading="loading"
    :error="error"
    @close="closeEditModal"
    @submit="handleUpdateClaim"
  />

  <!-- Approve Claim Modal -->
  <ApproveClaimModal
    :is-open="showApproveModal"
    :claim="claimToApprove"
    :loading="loading"
    @close="closeApproveModal"
    @confirm="confirmApprove"
  />

  <!-- Reject Claim Modal -->
  <RejectClaimModal
    :is-open="showRejectModal"
    :claim="claimToReject"
    :loading="loading"
    @close="closeRejectModal"
    @confirm="confirmReject"
  />

  <!-- Delete Claim Modal -->
  <DeleteClaimModal
    :is-open="showDeleteModal"
    :claim="claimToDelete"
    :loading="loading"
    @close="closeDeleteModal"
    @confirm="confirmDelete"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useAdminClaimStore } from '@/stores/adminClaimStore';
import { useBrand } from '@/composables/brand/useBrand';
import type { Claim, ClaimUpdateData } from '@/types/claim';
import ClaimFilters from '@/components/admin/ClaimFilters.vue';
import ClaimTable from '@/components/admin/ClaimTable.vue';
import CustomerPagination from '@/components/admin/CustomerPagination.vue';
import ClaimDetailsModal from '@/components/admin/ClaimDetailsModal.vue';
import ClaimEditModal from '@/components/admin/ClaimEditModal.vue';
import ApproveClaimModal from '@/components/admin/ApproveClaimModal.vue';
import RejectClaimModal from '@/components/admin/RejectClaimModal.vue';
import DeleteClaimModal from '@/components/admin/DeleteClaimModal.vue';
import SkeletonLoader from '@/components/common/SkeletonLoader.vue';

const toast = useToast();
const claimStore = useAdminClaimStore();
const { brandColors } = useBrand();

// Local state
const searchQuery = ref('');
const statusFilter = ref('');
const brandFilter = ref('');
const dateFromFilter = ref('');
const dateToFilter = ref('');
const showDetailsModal = ref(false);
const showEditModal = ref(false);
const showApproveModal = ref(false);
const showRejectModal = ref(false);
const showDeleteModal = ref(false);
const selectedClaim = ref<Claim | null>(null);
const claimToEdit = ref<Claim | null>(null);
const claimToApprove = ref<Claim | null>(null);
const claimToReject = ref<Claim | null>(null);
const claimToDelete = ref<Claim | null>(null);

// Computed properties from store
const claims = computed(() => claimStore.claims);
const loading = computed(() => claimStore.loading);
const error = computed(() => claimStore.error);
const pagination = computed(() => claimStore.pagination);
const underReviewClaims = computed(() => claimStore.underReviewClaims);
const approvedClaims = computed(() => claimStore.approvedClaims);
const rejectedClaims = computed(() => claimStore.rejectedClaims);

// Methods
function applyFilters() {
  claimStore.setFilters({
    search: searchQuery.value,
    status: statusFilter.value,
    brand: brandFilter.value ? Number(brandFilter.value) : undefined,
    date_from: dateFromFilter.value,
    date_to: dateToFilter.value,
    page: 1,
    per_page: 10
  });
  claimStore.fetchClaims();
}

function resetFilters() {
  searchQuery.value = '';
  statusFilter.value = '';
  brandFilter.value = '';
  dateFromFilter.value = '';
  dateToFilter.value = '';
  claimStore.resetFilters();
  claimStore.fetchClaims();
}

function goToPreviousPage() {
  if (pagination.value.current_page > 1) {
    claimStore.setFilters({ page: pagination.value.current_page - 1 });
    claimStore.fetchClaims();
  }
}

function goToNextPage() {
  if (pagination.value.current_page < pagination.value.last_page) {
    claimStore.setFilters({ page: pagination.value.current_page + 1 });
    claimStore.fetchClaims();
  }
}

function goToPage(page: number) {
  claimStore.setFilters({ page });
  claimStore.fetchClaims();
}

async function viewClaim(claim: Claim) {
  try {
    // Fetch full claim details with all relationships
    await claimStore.fetchClaim(claim.claim_id);
    selectedClaim.value = claimStore.selectedClaim;
    showDetailsModal.value = true;
  } catch (err) {
    toast.error('Failed to load claim details');
  }
}

function openApproveModal(claim: Claim) {
  claimToApprove.value = claim;
  showApproveModal.value = true;
}

function closeApproveModal() {
  showApproveModal.value = false;
  claimToApprove.value = null;
}

async function confirmApprove() {
  if (!claimToApprove.value) return;

  try {
    await claimStore.approveClaim(claimToApprove.value.claim_id);
    toast.success('Claim approved successfully');
    closeApproveModal();
    closeDetailsModal();
  } catch (err) {
    toast.error('Failed to approve claim');
  }
}

function openRejectModal(claim: Claim) {
  claimToReject.value = claim;
  showRejectModal.value = true;
}

function closeRejectModal() {
  showRejectModal.value = false;
  claimToReject.value = null;
}

async function confirmReject(rejectionReason: string) {
  if (!claimToReject.value) return;

  try {
    await claimStore.rejectClaim(claimToReject.value.claim_id, rejectionReason);
    toast.success('Claim rejected');
    closeRejectModal();
    closeDetailsModal();
  } catch (err) {
    toast.error('Failed to reject claim');
  }
}

function openDeleteModal(claim: Claim) {
  claimToDelete.value = claim;
  showDeleteModal.value = true;
}

function closeDeleteModal() {
  showDeleteModal.value = false;
  claimToDelete.value = null;
}

async function confirmDelete() {
  if (!claimToDelete.value) return;

  try {
    await claimStore.deleteClaim(claimToDelete.value.claim_id);
    toast.success('Claim deleted successfully');
    closeDeleteModal();
    closeDetailsModal();
  } catch (err) {
    toast.error('Failed to delete claim');
  }
}

function openEditModal(claim: Claim) {
  claimToEdit.value = claim;
  showEditModal.value = true;
}

function openEditModalFromDetails(claim: Claim) {
  claimToEdit.value = claim;
  showEditModal.value = true;
  showDetailsModal.value = false;
}

function closeEditModal() {
  showEditModal.value = false;
  claimToEdit.value = null;
}

async function handleUpdateClaim(data: ClaimUpdateData) {
  if (!claimToEdit.value) return;

  // If status is 'approved', show approve confirmation modal
  if (data.claim_status === 'approved') {
    claimToApprove.value = claimToEdit.value;
    showApproveModal.value = true;
    closeEditModal();
    return;
  }

  // If status is 'rejected', show reject confirmation modal
  if (data.claim_status === 'rejected') {
    claimToReject.value = claimToEdit.value;
    showRejectModal.value = true;
    closeEditModal();
    return;
  }

  // For other statuses (under review, ready for pick-up), update directly
  try {
    await claimStore.updateClaim(claimToEdit.value.claim_id, data);
    toast.success('Claim updated successfully');
    closeEditModal();
    closeDetailsModal();
  } catch (err) {
    toast.error('Failed to update claim');
  }
}

function closeDetailsModal() {
  showDetailsModal.value = false;
  selectedClaim.value = null;
}

// Lifecycle
onMounted(() => {
  // Reset filters to default when entering Claims Management
  claimStore.resetFilters();
  claimStore.fetchClaims();
});
</script>
